import Base from '../Base';
import Component from 'vue-class-component';
import ComponentHelper, { EComponentType, IComponent, IProject, IScene, IWebcamComponent, TComponentType } from '@/helpers/editor';
import AlertBoxTemplate from '@/components/editor/templates/AlertBoxTemplate.vue';
import ImageTemplate from '@/components/editor/templates/ImageTemplate.vue';
import TextTemplate from '@/components/editor/templates/TextTemplate.vue';
import VideoTemplate from '@/components/editor/templates/VideoTemplate.vue';
import CustomComponentTemplate from '@/components/editor/templates/CustomComponentTemplate.vue';
import ProjectApi from '@/apis/project-api';
import UserApi from '@/apis/user-api';
import WebcamTemplate from '@/components/editor/templates/WebcamTemplate.vue';
import EventListTemplate from '@/components/editor/templates/EventListTemplate.vue';
import ChatTemplate from '@/components/editor/templates/ChatTemplate.vue';
import { ITransition } from '@/helpers/transition';
import { ESocketEvent } from '@/helpers/socket';
import { Watch } from 'vue-property-decorator';
import SocketHelper from '@/helpers/socket';
import { io } from 'socket.io-client';
import VueSocketIOExt from 'vue-socket.io-extended';
import Vue from 'vue';
import StreamLabelTemplate from '../editor/templates/StreamLabelTemplate.vue';
import Peer from 'simple-peer';
import { IIceConfig } from '@/helpers/user';

const MAX_WEBCAM_DELAY = 3;

@Component({})
export default class OverlayPage extends Base {
  project: IProject = null;
  scene: IScene = null;
  uuid: string = null;
  isOverlayPreview: boolean = false;
  sessionId: string = null;
  isVertical: boolean = false;
  widgetToken: string = null;
  transition: ITransition = null;
  transitionPlaying: boolean = false;
  transitionDuration: number = 0;

  $refs: {
    transitionVideo: HTMLVideoElement;
  };
  socketServerUrlSetup: boolean = false;
  socketServerUrl: string = process.env.MIX_SOCKET_SERVER_URL;
  peerConnection = null;
  iceConfig: IIceConfig = null;
  stream: MediaStream = null;
  getIceConfigInterval: number = null;

  async mounted() {
    if (this.$route.query?.preview) this.isOverlayPreview = true;
    if (this.$route.query?.orientation === 'vertical') this.isVertical = true;
    if (this.$route.query?.session_id) this.sessionId = this.$route.query?.session_id as string;

    this.uuid = this.$route.params.uuid || null;
    const widgetTokenResponse = await UserApi.getUserWidgetTokenByIdentifier(this.uuid, this.sessionId);

    if (new URL(location.href).searchParams.get('overlayp')) {
      this.setSocketServerUrl('http://localhost:4001');
    } else if (widgetTokenResponse && widgetTokenResponse.success && widgetTokenResponse.socketServerUrl) {
      this.setSocketServerUrl(widgetTokenResponse.socketServerUrl);
    }

    if (widgetTokenResponse.success) {
      this.widgetToken = widgetTokenResponse.widgetToken;
    }

    await this.loadIceConfig();

    await this.loadActiveProjectScene();
    await this.connectToServer();
  }

  destroyed() {
    if (this.peerConnection) {
      this.sendSocketEvent(ESocketEvent.webcamPeerDisconnect);
      this.peerConnection.destroy();
    }

    clearInterval(this.getIceConfigInterval);
    this.getIceConfigInterval = null;
  }

  async loadActiveProjectScene() {
    const response = await ProjectApi.getUserActiveProjectScene(this.uuid, this.sessionId);
    this.project = response.project;
    this.scene = this.project?.active_scene;
    this.transition = this.project?.scene_transition;

    if (this.webcamComponent && this.stream) {
      this.onStream(this.stream);
    }
  }

  async loadSLWidgetToken() {
    const widgetTokenResponse = await UserApi.getUserWidgetTokenByIdentifier(this.uuid);

    if (widgetTokenResponse.success) {
      this.widgetToken = widgetTokenResponse.widgetToken;
    }
  }

  get hasProject(): boolean {
    return !!this.project;
  }

  get hasScene(): boolean {
    return !!this.scene;
  }

  get resolution(): number {
    if (!this.project?.settings?.resolution) return 720;

    return parseInt(this.project.settings.resolution);
  }

  get overlayPageHeightWidthCss() {
    if (this.isVertical) {
      return {
        '--width': this.resolution + 'px',
        '--height': ((this.resolution / 9) * 16) + 'px'
      };
    }

    return {
      '--height': this.resolution + 'px',
      '--width': ((this.resolution / 9) * 16) + 'px'
    };
  }

  get components(): IComponent[] {
    if (!this.hasScene) return [];

    if (!this.widgetToken && !this.isOverlayPreview) return this.scene.components.filter(component => !this.isSLComponent(component));

    let components = this.scene.components;

    if (this.isVertical) {
      components = this.scene.components.map((component) => {
        if (component.verticalSettings) {
          return {
            ...component,
            ...component.verticalSettings,
          };
        }

        const defaultSettings = ComponentHelper.getComponentSettingsByType(component.type);
        const verticalSettings = {
          x: defaultSettings?.verticalSettings?.x || 0,
          y: defaultSettings?.verticalSettings?.y || 0,
          w: defaultSettings?.verticalSettings?.w || component.w,
          h: defaultSettings?.verticalSettings?.h || component.h,
          lockAspectRatio: defaultSettings?.lockAspectRatio || false,
          aspectRatio: defaultSettings?.aspectRatio || 1,
        };

        return {
          ...component,
          ...verticalSettings,
        };
      });
    }

    return components;
  }

  get themeId(): number {
    if (!this.hasScene) return null;

    return this.scene.theme_id;
  }

  get transitionUrl(): string {
    return this.transition?.transition_url || null;
  }

  @Watch('transitionUrl')
  onTransitionChange() {
    if (!this.transitionUrl) return;

    this.$nextTick(() => {
      this.$refs.transitionVideo.onloadedmetadata = () => {
        this.transitionDuration = this.$refs.transitionVideo?.duration || 0;
      };

      this.$refs.transitionVideo.load();
    });
  }

  isTextComponent(component: IComponent): boolean {
    return component.type === EComponentType.text;
  }

  isImageComponent(component: IComponent): boolean {
    return component.type === EComponentType.image;
  }

  isAlertBoxComponent(component: IComponent): boolean {
    return component.type === EComponentType.alertBox;
  }

  isCustomComponent(component: IComponent): boolean {
    return component.type === EComponentType.customComponent;
  }

  isVideoComponent(component: IComponent): boolean {
    return component.type === EComponentType.video;
  }

  isWebcamComponent(component: IComponent): boolean {
    return component.type === EComponentType.webcam;
  }

  isStreamLabelComponent(component: IComponent): boolean {
    return component.type === EComponentType.streamLabel;
  }

  componentTemplate(component: IComponent) {
    switch (component.type) {
      case EComponentType.text:
        return TextTemplate;
      case EComponentType.image:
        return ImageTemplate;
      case EComponentType.video:
        return VideoTemplate;
      case EComponentType.alertBox:
        return AlertBoxTemplate;
      case EComponentType.customComponent:
        return CustomComponentTemplate;
      case EComponentType.webcam:
        return WebcamTemplate;
      case EComponentType.chat:
        return ChatTemplate;
      case EComponentType.eventList:
        return EventListTemplate;
      case EComponentType.streamLabel:
        return StreamLabelTemplate;
      default:
        return null;
    }
  }

  templateData(component: IComponent, index: number) {
    const data = {
      component,
      zIndex: this.components.length - index,
      widgetToken: this.widgetToken,
      isOverlayPreview: this.isOverlayPreview,
      themeId: this.themeId,
      uuid: this.uuid,
      sessionId: this.sessionId,
    };

    if (component.type === EComponentType.webcam) data.uuid = this.uuid;

    return data;
  }

  setSocketServerUrl(socketServerUrl: string) {
    if (this.socketServerUrlSetup) {
      return;
    }

    this.socketServerUrlSetup = true;
    this.socketServerUrl = socketServerUrl;
    const socketOptions = SocketHelper.socketOptions;
    const socket = io(socketServerUrl, socketOptions);
    Vue.use(VueSocketIOExt, socket);
  }

  componentsHaveWebcam(components: IComponent[] = this.components): boolean {
    return components.some((component) => this.isWebcamComponent(component));
  }

  @Watch('components')
  async handler(newVal, oldVal) {
    if (
      oldVal.length &&
      !this.componentsHaveWebcam(oldVal) &&
      this.componentsHaveWebcam(newVal) &&
      !this.peerConnection
    )
      await this.callEditor();
  }

  connectToServer() {
    this.$socket.client.io.opts.query = { accessCode: this.uuid };

    this.addSocketEventListener(ESocketEvent.updateScene, async () => {
      if (this.transitionUrl && this.$refs.transitionVideo) {
        this.transitionPlaying = true;
        this.$refs.transitionVideo.onplay = () => {
          this.project = null;
          this.scene = null;

          setTimeout(async () => {
            await this.loadActiveProjectScene();
          }, this.transitionDuration / 2 * 1000);
        };

        this.$refs.transitionVideo.play();

        setTimeout(() => {
          this.$refs.transitionVideo.pause();
          this.$refs.transitionVideo.currentTime = 0;
          this.transitionPlaying = false;
        }, this.transitionDuration * 1000);
      } else {
        await this.loadActiveProjectScene();
      }
    });

    this.addSocketEventListener(ESocketEvent.refreshOverlay, () => {
      window.location.reload();
    });

    this.addSocketEventListener(ESocketEvent.updateTransition, (transition) => {
      this.transition = transition;
    });

    this.addSocketEventListener(ESocketEvent.updateSLWidgetToken, this.loadSLWidgetToken);

    this.addSocketEventListener(ESocketEvent.webcamBroadcaster, this.onWebcamBroadcaster);
    this.addSocketEventListener(ESocketEvent.peerConnectionAccepted, this.onPeerConnectionAccepted);
    this.$socket.client.connect();
  }

  get webcamComponent(): IWebcamComponent {
    const webcamComponent = this.components.find((component) => this.isWebcamComponent(component));

    return <IWebcamComponent>webcamComponent || null;
  }

  async loadIceConfig() {
    try {
      const iceResponse = await UserApi.getIceConfig(this.uuid, this.sessionId);
      this.iceConfig = iceResponse.config;

      if (!this.getIceConfigInterval) {
        const intervalTime: any = new Date(iceResponse.config.expires_at).getTime() - new Date().getTime() - 100;
        this.getIceConfigInterval = setInterval(this.loadIceConfig, intervalTime);
      }
    } catch (error) {
    }
  }

  // call
  callEditor() {
    setTimeout(() => {
      this.sendSocketEvent(ESocketEvent.peerConnectionRequest, {
        from: this.$socket.client.id,
      });
    }, 500);
  }

  onWebcamBroadcaster() {
    if (this.componentsHaveWebcam()) this.callEditor();
  }

  onPeerConnectionAccepted(data) {
    const { broadcasterId } = data;

    // editor not opened
    if (!broadcasterId) return;

    // add socket event listener only if broadcaster is live
    this.addSocketEventListener(ESocketEvent.peerConnectionSignal, this.onPeerConnectionSignal);
    this.addSocketEventListener(ESocketEvent.webcamBroadcasterDisconnect, this.onWebcamBroadcasterDisconnect);

    if (this.peerConnection && this.peerConnection._connected) return;

    this.peerConnection = new Peer({
      initiator: true,
      config: { iceServers: [this.iceConfig] },
    });

    this.peerConnection.on('signal', (signal) => {
      this.sendSocketEvent(ESocketEvent.peerConnectionSignal, {
        from: this.$socket.client.id,
        to: broadcasterId,
        signal,
      });
    });

    this.peerConnection.on('stream', this.onStream);
    this.peerConnection.on('close', () => this.peerConnection = null);
  }

  onPeerConnectionSignal(data) {
    this.peerConnection.signal(data.signal);
  }

  onStream(stream: MediaStream) {
    this.stream = stream;

    let delay = this.webcamComponent?.delay || 0;
    if (delay && delay > MAX_WEBCAM_DELAY) delay = MAX_WEBCAM_DELAY;

    if (this.peerConnection) {
      for (const receiver of this.peerConnection._pc.getReceivers()) {
        receiver.playoutDelayHint = delay;
      }
    }

    this.$nextTick(() => {
      if (this.webcamComponent)
        this.$refs[this.webcamComponent.id][0].playStream(this.stream);
    });
  }

  onWebcamBroadcasterDisconnect() {
    this.stream = null;
    if (this.peerConnection) {
      this.peerConnection.destroy();
      this.peerConnection = null;
    }

    if (this.webcamComponent)
      this.$refs[this.webcamComponent.id][0].stopStream();
  }

  isSLComponent(component: IComponent) {
    const acceptableComponentTypes: TComponentType[] = [EComponentType.alertBox, EComponentType.chat, EComponentType.eventList, EComponentType.streamLabel];

    return acceptableComponentTypes.includes(component.type);
  }

  isComponentHidden(component: IComponent) {
    if (this.isVertical) return component.verticalSettings?.hidden;

    return component.hidden;
  }
}
